import * as React from 'react';
import { Button, ErrorState, LoadingState } from '@payment-plan/ui';
import { CatalogSteps } from './components/CatalogSteps';
import { ErrorReportTable } from './components/ErrorReportTable';
import { UploadDropzone } from './components/UploadDropzone';
import { useCatalogProjects, useConfirmCatalog, useValidateCatalog } from './use-catalog';
import { apiClient } from '../../../lib/api-client';
import type { CatalogMode, CatalogValidation } from '../../../types/admin';

const SELECT_CLASS =
  'h-10 w-full rounded-md border border-input bg-background px-3 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2';

/**
 * Carga del catálogo por Excel (HU-A03): validar → vista previa → confirmar. change-70: es una de las
 * dos vistas de `CatalogPage` y solo se monta con `catalogo.cargar` (quien no lo tiene no la ve).
 */
export function CatalogUpload(): React.JSX.Element {
  const { data: proyectos, isLoading, isError, refetch } = useCatalogProjects();
  const validate = useValidateCatalog();
  const confirm = useConfirmCatalog();

  const [proyectoId, setProyectoId] = React.useState('');
  const [modo, setModo] = React.useState<CatalogMode>('SOLO_NUEVAS');
  const [archivo, setArchivo] = React.useState<File | null>(null);
  const [validation, setValidation] = React.useState<CatalogValidation | null>(null);
  const [feedback, setFeedback] = React.useState<string | null>(null);

  const lista = proyectos?.data ?? [];

  React.useEffect(() => {
    if (!proyectoId && lista.length > 0) {
      setProyectoId(lista[0].id);
    }
  }, [lista, proyectoId]);

  const onFile = (file: File): void => {
    setArchivo(file);
    setValidation(null);
    setFeedback(null);
    if (proyectoId) {
      validate.mutate(
        { proyectoId, archivo: file },
        {
          onSuccess: setValidation,
          onError: (error) => setFeedback(error.message),
        },
      );
    }
  };

  const descargarPlantilla = async (): Promise<void> => {
    try {
      const blob = await apiClient.units.catalog.template();
      if (typeof URL.createObjectURL === 'function') {
        const url = URL.createObjectURL(blob);
        const anchor = document.createElement('a');
        anchor.href = url;
        anchor.download = 'plantilla-catalogo.xlsx';
        anchor.click();
        URL.revokeObjectURL(url);
      }
    } catch {
      setFeedback('No fue posible descargar la plantilla.');
    }
  };

  const confirmar = (): void => {
    const filas = validation?.filas ?? [];
    if (filas.length === 0) {
      return;
    }
    confirm.mutate(
      { proyectoId, modo, filas },
      {
        onSuccess: (resultado) =>
          setFeedback(
            `Carga confirmada: ${resultado.creadas} creadas, ${resultado.actualizadas} actualizadas.`,
          ),
        onError: (error) => setFeedback(error.message),
      },
    );
  };

  const current = confirm.isSuccess ? 3 : archivo ? 2 : 1;

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <CatalogSteps current={current} />
        <Button variant="outline" onClick={() => void descargarPlantilla()}>
          Descargar plantilla
        </Button>
      </div>

      {isLoading ? (
        <LoadingState message="Cargando proyectos" />
      ) : isError ? (
        <ErrorState message="No fue posible cargar los proyectos" onRetry={() => void refetch()} />
      ) : (
        <div className="grid gap-6 lg:grid-cols-[1fr_1.3fr]">
          <div className="space-y-4 rounded-xl border border-border/70 bg-card p-6 shadow-elegant">
            <div className="space-y-1.5">
              <label htmlFor="catalog-proyecto" className="block text-sm font-medium text-foreground">
                Proyecto
              </label>
              <select
                id="catalog-proyecto"
                aria-label="Proyecto"
                className={SELECT_CLASS}
                value={proyectoId}
                onChange={(event) => {
                  setProyectoId(event.target.value);
                  setValidation(null);
                }}
              >
                {lista.map((proyecto) => (
                  <option key={proyecto.id} value={proyecto.id}>
                    {proyecto.nombre}
                  </option>
                ))}
              </select>
            </div>
            <UploadDropzone onFile={onFile} fileName={archivo?.name} />
          </div>

          <div className="space-y-4">
            {validate.isPending && <LoadingState message="Validando archivo" />}
            {validation && (
              <div className="animate-rise space-y-4 rounded-xl border border-border/70 bg-card p-6 shadow-elegant">
                <p className="text-lg font-semibold tracking-tight text-foreground">
                  {validation.validas} unidades válidas
                </p>
                {validation.errores.length > 0 && <ErrorReportTable errores={validation.errores} />}
                <div className="flex flex-wrap items-end gap-3">
                  <div className="space-y-1.5">
                    <label htmlFor="catalog-modo" className="block text-sm font-medium text-foreground">
                      Modo de carga
                    </label>
                    <select
                      id="catalog-modo"
                      aria-label="Modo de carga"
                      className={SELECT_CLASS}
                      value={modo}
                      onChange={(event) => setModo(event.target.value as CatalogMode)}
                    >
                      <option value="SOLO_NUEVAS">Solo nuevas</option>
                      <option value="ACTUALIZAR_PRECIOS">Actualizar precios</option>
                    </select>
                  </div>
                  <Button
                    onClick={confirmar}
                    loading={confirm.isPending}
                    disabled={(validation.filas ?? []).length === 0}
                  >
                    Confirmar carga
                  </Button>
                </div>
              </div>
            )}
            {feedback && (
              <p
                role="status"
                className="rounded-lg border border-border/70 bg-surface-fade px-4 py-3 text-sm text-foreground"
              >
                {feedback}
              </p>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
